import React from "react";
import styles from "./personalisation-carousel.module.scss";
import Venn from "./venn";

const BREAKPOINT = 450;

const PersonalisationVis = props => {
    const { cardData, dimensions, opened } = props;

    const sameValues = React.useMemo(
        () => cardData.circles[0].value === cardData.circles[1].value,
        [cardData]
    );

    const center = React.useMemo(() => {
        const sameAndLarge = sameValues && cardData.circles[0].value >= 90;
        const desktopOffset = sameAndLarge ? -36 : -24;
        const mobileOffset = sameValues ? -16 : 28;
        return {
            x: dimensions.width / 2,
            y:
                dimensions.height / 2 +
                (dimensions.width < BREAKPOINT ? mobileOffset : desktopOffset),
        };
    }, [dimensions, sameValues, cardData]);

    const vennSet = React.useMemo(() => {
        const venn = new Venn(
            cardData.circles[0].value,
            cardData.circles[1].value,
            cardData.overlapPct,
            250
        );

        const aR = venn.aSetRadius || 25;
        const bR = venn.bSetRadius || 25;
        let centerSeparation = venn.setDistance;
        if (!venn.aSetRadius || !venn.bSetRadius) {
            centerSeparation = aR + bR + 8;
        }

        return {
            aR,
            bR,
            centerSeparation,
        };
    }, [cardData]);

    const aX = React.useMemo(() => center.x - vennSet.centerSeparation / 2, [center, vennSet]);
    const bX = React.useMemo(() => center.x + vennSet.centerSeparation / 2, [center, vennSet]);

    const vennBounds = React.useMemo(() => {
        const maxR = Math.max(vennSet.aR, vennSet.bR);
        const mob = dimensions.width < BREAKPOINT;
        return {
            left: aX - vennSet.aR,
            right: bX + vennSet.bR,
            top: center.y - maxR,
            bot: center.y + maxR,
            topA: mob ? 56 : center.y - vennSet.aR,
            topB: mob ? 56 : center.y - vennSet.bR,
            rightA: mob ? center.x : `calc(100% - ${aX}px + ${vennSet.aR}px)`,
            leftB: mob ? center.x : bX + vennSet.bR,
        };
    }, [center, vennSet, dimensions, aX, bX]);

    const textA = !sameValues && cardData.circles[0].label;
    const textB = !sameValues && cardData.circles[1].label;
    const textO = sameValues
        ? `${cardData.circles.map(d => d.label).join(" and ")}`
        : "fall into <em>both categories</em>";

    const clipId = cardData.id;
    return (
        <div className={styles.visOuter} data-open={opened}>
            <svg className={styles.svg}>
                <circle
                    className={styles.circle}
                    data-datum="a"
                    data-entered={opened}
                    cx={aX}
                    cy={center.y}
                    r={vennSet.aR}
                    data-fill={cardData.circles[0].value > 0}
                    style={{
                        transformOrigin: `${aX}px ${center.y}px`,
                    }}
                />
                <circle
                    className={styles.circle}
                    data-datum="b"
                    data-entered={opened}
                    cx={bX}
                    cy={center.y}
                    r={vennSet.bR}
                    data-fill={cardData.circles[1].value > 0}
                    style={{
                        transformOrigin: `${bX}px ${center.y}px`,
                    }}
                />
            </svg>
            <svg className={styles.overlapSvg}>
                <defs>
                    <clipPath id={clipId}>
                        <circle
                            className={styles.circle}
                            data-datum="a"
                            data-entered={opened}
                            cx={aX}
                            cy={center.y}
                            r={vennSet.aR}
                            style={{
                                transformOrigin: `${aX}px ${center.y}px`,
                            }}
                        />
                    </clipPath>
                </defs>

                <g clipPath={`url(#${clipId})`}>
                    <circle
                        className={styles.circle}
                        data-datum="overlap"
                        data-entered={opened}
                        cx={bX}
                        cy={center.y}
                        r={vennSet.bR}
                        style={{
                            transformOrigin: `${bX}px ${center.y}px`,
                        }}
                    />
                </g>
            </svg>
            <div className={styles.visText} data-entered={opened}>
                {textA && (
                    <div
                        className={styles.valueTextContainer}
                        style={{
                            left: 0,
                            right: vennBounds.rightA,
                            top: vennBounds.topA,
                        }}
                    >
                        <div className={styles.valueTextPercent} data-datum="a">
                            {cardData.circles[0].value.toFixed(0)}%
                        </div>
                        <div
                            className={styles.valueTextDescription}
                            dangerouslySetInnerHTML={{
                                __html: textA,
                            }}
                        />
                    </div>
                )}
                {textB && (
                    <div
                        className={styles.valueTextContainer}
                        style={{
                            left: vennBounds.leftB,
                            right: 0,
                            top: vennBounds.topB,
                        }}
                    >
                        <div className={styles.valueTextPercent} data-datum="b">
                            {cardData.circles[1].value.toFixed(0)}%
                        </div>
                        <div
                            className={styles.valueTextDescription}
                            dangerouslySetInnerHTML={{
                                __html: textB,
                            }}
                        />
                    </div>
                )}
                {textO && (
                    <div
                        className={styles.valueTextContainer}
                        style={{
                            left: "50%",
                            bottom: `calc(100% - ${vennBounds.bot}px)`,
                        }}
                        data-overlap
                    >
                        <div className={styles.valueTextPercent} data-datum="overlap">
                            {cardData.overlapPct.toFixed(0)}%
                        </div>
                        <div
                            className={styles.valueTextDescription}
                            data-wide={sameValues}
                            dangerouslySetInnerHTML={{
                                __html: textO,
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PersonalisationVis;
